import type { DigestInterval } from "./types/digest";
import type { CatalystEventType } from "./constants/catalystEventTypes";

// Ready-made digests a user can start from instead of writing a prompt. A
// template only prefills the New Digest form; nothing is stored until the user
// saves, and the saved digest is an ordinary config they own.
export interface DigestTemplate {
  id: string;
  name: string;
  description: string;
  prioritisation_prompt: string;
  event_type_filters: readonly CatalystEventType[];
  interval: DigestInterval;
  anchor_day: number; // 0-6 (Mon-Sun), in the user's local time
  anchor_time: string; // HH:MM, in the user's local time
}

const WEEKLY_MONDAY_MORNING = {
  interval: "weekly",
  anchor_day: 0,
  anchor_time: "09:00",
} as const;

export const DIGEST_TEMPLATES: readonly DigestTemplate[] = [
  {
    id: "weekly-biotech-intelligence",
    name: "Weekly Biotech Intelligence",
    description:
      "The week's most important biotech news: fundraising, FDA and EMA decisions, new company launches and negative news or data.",
    prioritisation_prompt:
      "Prioritise the most important biotech industry updates of the period: fundraising announcements and IPOs, major FDA or EMA decisions and guidance, new company launches, pivotal clinical data, and companies receiving negative news or data such as failed trials, discontinued programmes, rejections or complete response letters. Favour large financings, late-stage and registrational results, and regulatory decisions over routine updates. Keep each summary concise and easy to read.",
    event_type_filters: [
      "Funding Round",
      "Public Offering",
      "Merger or Acquisition",
      "Company Spin-off",
      "Clinical Trial Data Readout",
      "Phase 3 Completion",
      "Regulatory Submission",
      "Regulatory Approval",
      "Complete Response Letter",
      "Regulatory Rejection",
      "Regulatory Policy Change",
    ],
    ...WEEKLY_MONDAY_MORNING,
  },
  {
    id: "fundraising-new-companies",
    name: "Fundraising & New Companies",
    description:
      "Venture rounds, IPOs, acquisitions and newly launched companies.",
    prioritisation_prompt:
      "Prioritise private financing rounds, IPOs and follow-on offerings, acquisitions and newly launched or spun-out companies. Rank by the size of the financing and how notable the investors are, and state the amount raised, the round and what the money will fund whenever it is known.",
    event_type_filters: [
      "Funding Round",
      "Public Offering",
      "Merger or Acquisition",
      "Company Spin-off",
      "Government Grant",
    ],
    ...WEEKLY_MONDAY_MORNING,
  },
  {
    id: "fda-ema-developments",
    name: "FDA & EMA Developments",
    description:
      "Approvals, rejections, complete response letters, submissions, designations and policy changes.",
    prioritisation_prompt:
      "Prioritise regulatory developments from the FDA and EMA: approvals and label expansions, rejections and complete response letters, submissions, special designations and changes in regulatory policy or guidance. Rank final decisions above submissions and designations, and state the product, the indication and the key supporting result whenever it is known.",
    event_type_filters: [
      "Regulatory Submission",
      "Regulatory Meeting",
      "Regulatory Approval",
      "Orphan Drug Designation",
      "Fast Track Designation",
      "Breakthrough Therapy Designation",
      "Accelerated Approval Designation",
      "Complete Response Letter",
      "Regulatory Rejection",
      "Regulatory Policy Change",
    ],
    ...WEEKLY_MONDAY_MORNING,
  },
  {
    id: "clinical-winners-setbacks",
    name: "Clinical Data: Winners & Setbacks",
    description:
      "Positive and negative clinical readouts, with late-stage results first.",
    prioritisation_prompt:
      "Prioritise clinical trial results, both positive and negative: trials that met or missed their primary endpoint, studies stopped for futility or safety, and data that changes the outlook for a programme. Rank Phase 3 and registrational results above earlier phases, and state the asset, the indication, the phase and the headline result.",
    event_type_filters: [
      "Clinical Trial Data Readout",
      "Phase 2 Completion",
      "Phase 3 Completion",
      "Major Scientific Presentation",
    ],
    ...WEEKLY_MONDAY_MORNING,
  },
];

export function getDigestTemplate(
  id: string | null | undefined,
): DigestTemplate | undefined {
  if (!id) return undefined;
  return DIGEST_TEMPLATES.find((template) => template.id === id);
}
